import React from 'react';
import type {ValueOf} from 'type-fest';
import type {KnockGuide} from '@knocklabs/client';
import {useGuides} from '@knocklabs/react';
import type {Telemetry} from '@packages/systems/telemetry';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_PROPERTIES,
} from '@packages/systems/core/constants/analytics';
import {
  Button,
  type ButtonColorVariant,
  type TextColor,
  type IconComponent,
} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';
import {BrandedButton} from '@packages/systems/spring-branded';
import {KnockMessageType} from './KnockMessageType';
import {useGuide} from './hooks/useGuide';
import {useHandleKnockAction} from './hooks/useHandleKnockAction';
import {TelemetryContext} from './NotificationsProvider';
import {knockIconMap} from './knockIconMap';
import type {KnockButton} from './types';

type SlotName = ValueOf<typeof KnockMessageType>;

type GuideContent = {
  // available in all variants
  icon?: string;
  campaign: string;
  closeColor?: ButtonColorVariant;
  layout: string;
  title: string;
  titleCondensed?: string;
  subtitle?: string;
  content?: string;
  theme: 'blue' | 'black';

  // available for image upload
  image?: {
    action?: string;
    url: string;
    alt?: string;
  };

  // available in link variant
  link?: string;
  linkText?: string;

  // available in button variant
  button?: KnockButton;

  primaryButton?: KnockButton;
  secondaryButton?: KnockButton;
};

type SlotComponent = () => React.ReactNode;
type SlotVariantComponent = (props: SlotComponentProps) => React.ReactNode;

type SlotConfig = {
  analytics: {
    source: ValueOf<typeof ANALYTICS_PROPERTIES.SURFACE_AREA>;
    pageSection: ValueOf<typeof ANALYTICS_PROPERTIES.PAGE_SECTION>;
    position: ValueOf<typeof ANALYTICS_PROPERTIES.PAGE_POSITION>;
    ctaClickEvent: ValueOf<typeof ANALYTICS_EVENTS>;
  };
  slot: SlotName;
  layouts: Record<string, SlotVariantComponent>;
};

type SlotWrapperProps = {
  analytics: SlotConfig['analytics'];
  Component: SlotVariantComponent;
  guide: ReturnType<typeof useGuide>;
  telemetry: Telemetry<unknown> | null;
};

export function createNotificationSlot({
  analytics,
  slot,
  layouts,
}: SlotConfig): SlotComponent {
  function NotificationSlot() {
    const isIPNBetaEnabled = useFeatureFlag(FEATURE_FLAGS.IPN_SLOT_MECHANISM);
    const telemetry = React.useContext(TelemetryContext);

    // we skip mark as seen here, because we only want to mark a guide as seen
    // when the user actually views the content, and at this point we don't
    // know if the requested layout component is supported.
    const guide = useGuide({type: slot}, {skipMarkAsSeen: true});

    if (!isIPNBetaEnabled || !guide || !guide.step || !guide.step.content) {
      return null;
    }

    const content = guide.step.content as GuideContent;

    const Component = layouts[content.layout];

    if (!Component) {
      telemetry?.sendError(
        `No component found for slot "${slot}" with layout "${content.layout}"`
      );
      return null;
    }

    return (
      <SlotWrapper
        analytics={analytics}
        Component={Component}
        guide={guide}
        telemetry={telemetry}
      />
    );
  }

  NotificationSlot.displayName = `NotificationSlot(${slot})`;

  return NotificationSlot;
}

export type SlotComponentProps = GuideContent & {
  onClick: (button: {action: string; text: string}) => void;
  markAsSeen: () => void;
  telemetry?: Telemetry<unknown> | null;
};

function SlotWrapper({
  analytics,
  Component,
  guide,
  telemetry,
}: SlotWrapperProps) {
  const {step} = guide;

  // mark the step as seen when the component is mounted, since at
  // this point we know the layout component is supported!
  React.useEffect(() => {
    if (step) step.markAsSeen();
  }, [step]);

  const handleAction = useHandleKnockAction(guide, analytics, telemetry);

  if (!step || !step.content) {
    // impossible state since this is checked in the slot component
    // but we handle it gracefully here to appease TypeScript
    return null;
  }

  const content = step.content as GuideContent;

  return (
    <Component
      {...content}
      onClick={handleAction}
      telemetry={telemetry}
      markAsSeen={() => step.markAsSeen()}
    />
  );
}

export type ContainerProps = {
  analytics: SlotConfig['analytics'];
  guides: KnockGuide[];
  telemetry: Telemetry<unknown> | null;
};
type MultiSlotComponent = (props: ContainerProps) => React.ReactNode;

type MultiSlotConfig = {
  analytics: SlotConfig['analytics'];
  slot: SlotName;
  components: {
    MultipleItems: MultiSlotComponent;
    SingleItem?: SlotVariantComponent;
  };
};

type GuideListItemProps = {
  analytics: SlotConfig['analytics'];
  Component: SlotVariantComponent;
  guide: KnockGuide;
  telemetry: Telemetry<unknown> | null;
};

export function createMultiNotificationSlot({
  analytics,
  slot,
  components,
}: MultiSlotConfig): SlotComponent {
  function NotificationSlot() {
    const isIPNBetaEnabled = useFeatureFlag(FEATURE_FLAGS.IPN_SLOT_MECHANISM);
    const telemetry = React.useContext(TelemetryContext);

    // we skip mark as seen here, because we only want to mark a guide as seen
    // when the user actually views the content, and at this point we don't
    // know if the requested layout component is supported.
    const {guides} = useGuides({type: slot});

    // also grab the first guide to ensure that prioritisation works correctly.
    // currently, knock ignores the `useGuides` hook when figuring out which
    // components should render, and only the `useGuide` hook works. we can
    // remove this if/when knock updates their sdk to fix this!
    const shouldRender = useGuide({type: slot}, {skipMarkAsSeen: true});

    if (!isIPNBetaEnabled || !guides || !guides.length || !shouldRender.guide) {
      return null;
    }

    if (guides.length === 1 && components.SingleItem) {
      const [guide] = guides;
      const {SingleItem} = components;
      return guide ? (
        <GuideListItem
          analytics={analytics}
          Component={SingleItem}
          guide={guide}
          telemetry={telemetry}
        />
      ) : null;
    }

    const {MultipleItems} = components;
    return (
      <MultipleItems
        analytics={analytics}
        guides={guides}
        telemetry={telemetry}
      />
    );
  }

  NotificationSlot.displayName = `NotificationSlot(${slot})`;

  return NotificationSlot;
}

export function GuideListItem({
  analytics,
  Component,
  guide,
  telemetry,
}: GuideListItemProps) {
  const step = guide.getStep();
  const _guide = React.useMemo(() => {
    return {
      guide,
      step,
    };
  }, [guide, step]);

  const handleAction = useHandleKnockAction(_guide, analytics, telemetry);

  if (!guide || !step || !step.content) {
    // impossible state since this is checked in the slot component
    // but we handle it gracefully here to appease TypeScript
    return null;
  }

  const content = step.content as GuideContent;

  return (
    <Component
      {...content}
      onClick={handleAction}
      telemetry={telemetry}
      markAsSeen={() => step.markAsSeen()}
    />
  );
}

type GuideButtonProps = {
  button: KnockButton;
  buttonVariant?: 'spring' | 'branded-font' | 'branded-full';
  onClick: (button: KnockButton) => void;
  iconColorVariant?: TextColor;
  linkIcon?: IconComponent;
} & Omit<React.ComponentProps<typeof Button>, 'onClick'>;

const BrandedSpringButton = emotionStyled(Button)({
  fontFamily: '$brandedBody',
});
const buttonVariants = {
  spring: Button,
  'branded-font': BrandedSpringButton as typeof Button,
  'branded-full': BrandedButton,
} as const;

export function GuideButton({
  button,
  buttonVariant = 'spring',
  onClick,
  iconColorVariant = 'inverse',
  linkIcon: LinkIcon,
  ...props
}: GuideButtonProps) {
  const {action, text} = button;
  const Icon = knockIconMap[action];
  const ButtonComponent = buttonVariants[buttonVariant];
  return action.startsWith('http') ? (
    <ButtonComponent
      href={action}
      target="_blank"
      onClick={() => onClick({action: 'url', text})}
      suffix={LinkIcon && <LinkIcon colorVariant={iconColorVariant} />}
      {...props}
    >
      {text}
    </ButtonComponent>
  ) : (
    <ButtonComponent
      onClick={() => onClick(button)}
      suffix={Icon && <Icon colorVariant={iconColorVariant} />}
      {...props}
    >
      {text}
    </ButtonComponent>
  );
}
